import { AppShell, Burger, Group, Loader, Menu, NavLink, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { NavLink as RouterLink, Navigate, Route, Routes, useLocation } from 'react-router'
import {
  IconBuilding, IconBuildingWarehouse, IconCash, IconChecklist, IconClipboardList, IconCrane,
  IconFileInvoice, IconLogout, IconSitemap, IconReportAnalytics, IconStars, IconUserCircle, IconUsers,
} from '@tabler/icons-react'
import { CambiarPasswordPagina } from './modulos/auth/CambiarPasswordPagina'
import { LoginPagina } from './modulos/auth/LoginPagina'
import { useCerrarSesion, useSesion } from './modulos/auth/useSesion'
import { EmpresasPagina } from './modulos/empresas/EmpresasPagina'
import { ObrasPagina } from './modulos/obras/ObrasPagina'
import { AreasPagina } from './modulos/areas/AreasPagina'
import { UsuariosPagina } from './modulos/usuarios/UsuariosPagina'
import { FichasPagina } from './modulos/fichas/FichasPagina'
import { OrdenesPagina } from './modulos/ordenes/OrdenesPagina'
import { PagosPagina } from './modulos/pagos/PagosPagina'
import { ReportesPagina } from './modulos/reportes/ReportesPagina'
import { ProveedoresPagina } from './modulos/proveedores/ProveedoresPagina'
import { ValidacionesPagina } from './modulos/validaciones/ValidacionesPagina'
import { RequerimientosPagina } from './modulos/requerimientos/RequerimientosPagina'

/**
 * Una entrada por módulo. Agregar una pestaña es agregar una línea acá y una
 * carpeta en src/modulos/. Ningún otro archivo central cambia.
 */
const PESTANAS = [
  { ruta: '/requerimientos', etiqueta: 'Requerimientos', icono: IconClipboardList,     listo: true,  soloAdmin: false },
  { ruta: '/ordenes',        etiqueta: 'Órdenes',        icono: IconFileInvoice,       listo: true,  soloAdmin: false },
  { ruta: '/validaciones',   etiqueta: 'Validaciones',   icono: IconChecklist,         listo: true,  soloAdmin: false },
  { ruta: '/proveedores',    etiqueta: 'Proveedores',    icono: IconBuildingWarehouse, listo: true,  soloAdmin: false },
  { ruta: '/evaluaciones',   etiqueta: 'Evaluación',     icono: IconStars,             listo: true,  soloAdmin: false },
  { ruta: '/obras',          etiqueta: 'Obras',          icono: IconCrane,             listo: true,  soloAdmin: false },
  { ruta: '/areas',          etiqueta: 'Áreas',          icono: IconSitemap,           listo: true,  soloAdmin: false },
  { ruta: '/pagos',          etiqueta: 'Pagos',          icono: IconCash,              listo: true,  soloAdmin: false },
  { ruta: '/reportes',       etiqueta: 'Reportes',       icono: IconReportAnalytics,   listo: true,  soloAdmin: false },
  { ruta: '/empresas',       etiqueta: 'Empresas',       icono: IconBuilding,          listo: true,  soloAdmin: true },
  { ruta: '/usuarios',       etiqueta: 'Usuarios',       icono: IconUsers,             listo: true,  soloAdmin: true },
] as const

function EnConstruccion({ nombre }: { nombre: string }) {
  return (
    <Stack gap="xs">
      <Title order={2}>{nombre}</Title>
      <Text c="dimmed">Este módulo aún no está construido.</Text>
    </Stack>
  )
}

export function App() {
  const { usuario, cargando } = useSesion()
  const cerrar = useCerrarSesion()
  const [abierto, { toggle }] = useDisclosure()
  const ubicacion = useLocation()

  if (cargando) {
    return (
      <Stack align="center" justify="center" mih="100vh">
        <Loader />
      </Stack>
    )
  }

  if (!usuario) return <LoginPagina />

  // Corte total hasta cambiar la contraseña. El backend hace lo mismo con 403;
  // esto solo evita mostrar una interfaz que no va a responder.
  if (usuario.debeCambiarPassword) return <CambiarPasswordPagina />

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 230, breakpoint: 'sm', collapsed: { mobile: !abierto } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={abierto} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={650}>Seguimiento OC / OS</Text>
          </Group>

          <Menu position="bottom-end">
            <Menu.Target>
              <Group gap={6} style={{ cursor: 'pointer' }}>
                <IconUserCircle size={20} />
                <Stack gap={0}>
                  <Text size="sm" fw={550}>
                    {usuario.nombres} {usuario.apellidos}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {usuario.cargo ?? usuario.rol}
                  </Text>
                </Stack>
              </Group>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconLogout size={16} />}
                onClick={() => cerrar.mutate()}
              >
                Cerrar sesión
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        {PESTANAS.filter((p) => !p.soloAdmin || usuario.rol === 'ADMIN')
          .map(({ ruta, etiqueta, icono: Icono, listo }) => (
          <NavLink
            key={ruta}
            component={RouterLink}
            to={ruta}
            label={etiqueta}
            leftSection={<Icono size={18} />}
            active={ubicacion.pathname.startsWith(ruta)}
            disabled={!listo}
            description={listo ? undefined : 'Próximamente'}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<Navigate to="/requerimientos" replace />} />
          <Route path="/requerimientos" element={<RequerimientosPagina />} />
          <Route path="/ordenes" element={<OrdenesPagina />} />
          <Route path="/validaciones" element={<ValidacionesPagina />} />
          <Route path="/pagos" element={<PagosPagina />} />
          <Route path="/evaluaciones" element={<FichasPagina />} />
          <Route path="/reportes" element={<ReportesPagina />} />
          <Route path="/proveedores" element={<ProveedoresPagina />} />
          <Route path="/obras" element={<ObrasPagina />} />
          <Route path="/areas" element={<AreasPagina />} />
          <Route path="/empresas" element={<EmpresasPagina />} />
          <Route path="/usuarios" element={<UsuariosPagina />} />
          {PESTANAS.filter((p) => !p.listo).map((p) => (
            <Route key={p.ruta} path={p.ruta} element={<EnConstruccion nombre={p.etiqueta} />} />
          ))}
          <Route path="*" element={<Navigate to="/requerimientos" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  )
}
